import type { ReactNode } from 'react';
import {
  StatusSuccessIcon,
  StatusErrorIcon,
  StatusWarningIcon,
  Flex,
  StatusInfoIcon,
  Text,
} from '@/uicomponents';
import { Show } from '@/components/shared/show';
import styles from './state-panel.module.css';

export type StatePanelTone = 'success' | 'warning' | 'error' | 'info';

export interface StatePanelProps {
  tone: StatePanelTone;
  title?: string;
  children?: ReactNode;
  actions?: ReactNode;
}

const TONE_CLASS: Record<StatePanelTone, string> = {
  success: styles.success,
  warning: styles.warning,
  error: styles.error,
  info: styles.info,
};

const TONE_ICON: Record<StatePanelTone, ReactNode> = {
  success: <StatusSuccessIcon aria-hidden />,
  warning: <StatusWarningIcon aria-hidden />,
  error: <StatusErrorIcon aria-hidden />,
  info: <StatusInfoIcon aria-hidden />,
};

const TONE_ROLE: Record<StatePanelTone, 'alert' | 'status'> = {
  success: 'status',
  warning: 'status',
  error: 'alert',
  info: 'status',
};

const TONE_LIVE: Record<StatePanelTone, 'polite' | 'assertive'> = {
  success: 'polite',
  warning: 'polite',
  error: 'assertive',
  info: 'polite',
};

export const StatePanel = ({ tone, title, children, actions }: StatePanelProps) => {
  return (
    <Flex
      gap={12}
      align='flex-start'
      className={`${styles.panel} ${TONE_CLASS[tone]}`}
      role={TONE_ROLE[tone]}
      aria-live={TONE_LIVE[tone]}
    >
      <Flex className={styles.icon}>{TONE_ICON[tone]}</Flex>
      <Flex vertical gap={8} flex={1}>
        <Show when={!!title}>
          <Text variant='bodyLg' weight='semibold'>
            {title}
          </Text>
        </Show>
        <Show when={!!children}>
          <Text variant='body'>{children}</Text>
        </Show>
        <Show when={!!actions}>
          <Flex gap={8} wrap>
            {actions}
          </Flex>
        </Show>
      </Flex>
    </Flex>
  );
};
